import { FC, useCallback, useState } from 'react'
import { useCreateContractContext, useUserLanguage } from '@/customHooks'
import { NormalFormNumberField, SquareButtonsCard } from '../molecules'
import CanCelIcon from '@/assets/contract-icons/cancel-icon.svg'
import ConfirmIcon from '@/assets/contract-icons/confirm-icon.svg'
import textsData from '@/textData/CreateContractsTexts.json'

const fieldContainerClass = "bg-[var(--color-light)] rounded-lg p-8 shadow-[0_5px_10px_rgba(0,0,0,0.1)] w-full max-w-[650px] flex flex-col gap-4"

export const SelectContractLateFees: FC = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es.lateFees : textsData.en.lateFees

  const {
    state: contractState,
    setLateFees,
    setButtonEnabled,
    setButtonDisabled,
  } = useCreateContractContext()

  const [isHidden, setIsHidden] = useState<{ percentageForm: boolean; amountForm: boolean }>({
    percentageForm: contractState.percentageOfRentFees === 0 && contractState.fiatAmountFees === 0,
    amountForm: true // Default view, logic in onSelectedHandle will adjust
  })

  // Initial state logic for isHidden if some value already exists in context
  useState(() => {
    if (contractState.percentageOfRentFees && contractState.percentageOfRentFees > 0) {
      setIsHidden({ percentageForm: false, amountForm: true })
    } else if (contractState.fiatAmountFees && contractState.fiatAmountFees > 0) {
      setIsHidden({ percentageForm: true, amountForm: false })
    }
  })

  const buttons = [
    { text: texts.button.first, icon: ConfirmIcon },
    { text: texts.button.second, icon: ConfirmIcon },
    { text: texts.button.third, icon: CanCelIcon },
  ];

  const handleDataChange = useCallback((field: 'days' | 'percentage' | 'amount', value: string | null) => {
    const val = parseInt(value || '0') || 0;
    
    let days = contractState.lateFeeDays || 0;
    let percentage = contractState.percentageOfRentFees || 0;
    let amount = contractState.fiatAmountFees || 0;

    if (field === 'days') days = val;
    if (field === 'percentage') {
      percentage = val;
      amount = 0; // Reset other type
    }
    if (field === 'amount') {
      amount = val;
      percentage = 0; // Reset other type
    }

    setLateFees(days, percentage, amount);
  }, [contractState.fiatAmountFees, contractState.lateFeeDays, contractState.percentageOfRentFees, setLateFees]);

  const onSelectedHandle = (value: number | null) => {
    switch (value) {
      case null:
        setButtonDisabled()
        break

      case 0:
        setIsHidden({ percentageForm: false, amountForm: true })
        setButtonEnabled()
        // Initialize with current or default
        handleDataChange('percentage', contractState.percentageOfRentFees?.toString() ?? '0')
        break
      
      case 1:
        setIsHidden({ percentageForm: true, amountForm: false })
        setButtonEnabled()
        // Initialize with current or default
        handleDataChange('amount', contractState.fiatAmountFees?.toString() ?? '0')
        break
      
      case 2:
        setIsHidden({ percentageForm: true, amountForm: true })
        setButtonEnabled()
        setLateFees(0, 0, 0)
        break
      
      default:
        throw new Error('Select value for late fees is invalid [SelectContractLateFees]')
    }
  }

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex justify-center w-full">
        <SquareButtonsCard
          buttons={buttons}
          onSelected={onSelectedHandle}
        />
      </div>

      <div className="mt-12 flex justify-center w-full">
        {!isHidden.percentageForm && (
          <div className={fieldContainerClass}>
            <NormalFormNumberField
              label={texts.form1.applyDays.label}
              name={texts.form1.applyDays.name}
              value={contractState.lateFeeDays?.toString()}
              onChange={(val) => handleDataChange('days', val)}
              required
            />
            <NormalFormNumberField
              label={texts.form1.percentage.label}
              name={texts.form1.percentage.name}
              value={contractState.percentageOfRentFees?.toString()}
              onChange={(val) => handleDataChange('percentage', val)}
              required
            />
          </div>
        )}

        {!isHidden.amountForm && (
          <div className={fieldContainerClass}>
            <NormalFormNumberField
              label={texts.form2.applyDays.label}
              name={texts.form2.applyDays.name}
              value={contractState.lateFeeDays?.toString()}
              onChange={(val) => handleDataChange('days', val)}
              required
            />
            <NormalFormNumberField
              label={texts.form2.amount.label}
              name={texts.form2.amount.name}
              value={contractState.fiatAmountFees?.toString()}
              onChange={(val) => handleDataChange('amount', val)}
              required
            />
          </div>
        )}
      </div>
    </div>
  );
};
